import { signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';

/**
 * A keyed population of disposables that follows a set of wanted ids: each id owns one
 * {@link DisposableBox}, so whatever is created for an id goes away when the id stops being wanted
 * or the map is disposed. `current` publishes the live members for signals to read.
 */
export class DisposableBoxMap<T extends Disposable> implements Disposable {
    readonly #boxes = new Map<string, DisposableBox<T>>();
    readonly #current = signal<ReadonlyMap<string, T>>(new Map());

    get current(): ReadonlySignal<ReadonlyMap<string, T>> {
        return this.#current;
    }

    /** Dispose members no longer wanted and create the missing ones. Returns whether anything changed. */
    reconcile(wanted: ReadonlySet<string>, create: (id: string) => T): boolean {
        let changed = false;
        for (const [id, box] of this.#boxes) {
            if (wanted.has(id)) continue;
            box.clear();
            this.#boxes.delete(id);
            changed = true;
        }
        for (const id of wanted) {
            if (this.#boxes.has(id)) continue;
            this.#boxes.set(id, new DisposableBox(() => create(id)));
            changed = true;
        }
        if (changed) this.#publish();
        return changed;
    }

    #publish(): void {
        this.#current.value = new Map(
            [...this.#boxes].flatMap(([id, box]) => (box.current ? [[id, box.current] as const] : [])),
        );
    }

    [Symbol.dispose](): void {
        for (const box of this.#boxes.values()) box.clear();
        this.#boxes.clear();
        this.#publish();
    }
}
